import { call, put, select, takeLatest, delay } from 'redux-saga/effects';
import * as constants from './constants';
import * as actions from './actions';
import * as api from './api';

/**
 * Saga to play a track
 */
function* playTrackSaga(action) {
  try {
    const { trackId, tourId } = action.payload;

    // Grab the JWT so authenticated endpoints don't return 401
    const accessToken = yield select(state => state.Auth.accessToken);

    // Get track data (includes presigned S3 URL)
    const trackData = yield call(api.getTrack, trackId, accessToken);

    // Start playback tracking
    const playbackData = yield call(api.startPlayback, trackId, accessToken);

    // Map streamUrl (backend field) → audioUrl (AudioPlayer field).
    // tourTitle is nested under trackData.tour.title in the API response
    // (GET /audio/tracks/{id} returns { ..., tour: { id, title, ... } }).
    // Hoist it to the top level so AudioPlayer can read currentTrack.tourTitle directly.
    const track = {
      ...trackData,
      audioUrl: trackData.streamUrl,
      tourTitle: trackData.tour?.title,
      tourId,
    };

    // Backend returns eventId (camelCase)
    yield put(actions.playTrackSuccess(track, playbackData.eventId));

    // Start heartbeat loop (every 10 seconds)
    yield call(heartbeatLoop);
  } catch (error) {
    console.error('Error playing track:', error);
    yield put(actions.playTrackFailure(error.message));
  }
}

/**
 * Heartbeat loop - sends playback progress every 10 seconds
 */
function* heartbeatLoop() {
  while (true) {
    yield delay(10000); // Wait 10 seconds

    // Get current state
    const audioState = yield select(state => state.audio);

    // Only send heartbeat if still playing
    if (audioState.isPlaying && audioState.playbackEventId) {
      try {
        yield call(
          api.sendHeartbeat,
          audioState.playbackEventId,
          Math.floor(audioState.currentTime)
        );
        yield put(actions.sendPlaybackHeartbeatSuccess());
      } catch (error) {
        console.error('Heartbeat failed:', error);
        yield put(actions.sendPlaybackHeartbeatFailure(error.message));
      }
    } else {
      // Stop heartbeat loop if not playing
      break;
    }
  }
}

/**
 * Saga to send heartbeat
 */
function* sendHeartbeatSaga(action) {
  try {
    const { eventId, secondsListened } = action.payload;
    yield call(api.sendHeartbeat, eventId, secondsListened);
    yield put(actions.sendPlaybackHeartbeatSuccess());
  } catch (error) {
    console.error('Error sending heartbeat:', error);
    yield put(actions.sendPlaybackHeartbeatFailure(error.message));
  }
}

/**
 * Saga to play next track
 */
function* playNextTrackSaga() {
  const audioState = yield select(state => state.audio);
  const { tourTracks, currentTrackIndex } = audioState;

  if (currentTrackIndex < tourTracks.length - 1) {
    const nextTrack = tourTracks[currentTrackIndex + 1];
    yield put(actions.playTrackRequest(nextTrack.trackId, audioState.currentTour?.tourId));
  } else {
    // End of tour - stop playback
    yield put(actions.stopTrack());
  }
}

/**
 * Saga to play previous track
 */
function* playPreviousTrackSaga() {
  const audioState = yield select(state => state.audio);
  const { tourTracks, currentTrackIndex } = audioState;

  if (currentTrackIndex > 0) {
    const previousTrack = tourTracks[currentTrackIndex - 1];
    yield put(actions.playTrackRequest(previousTrack.trackId, audioState.currentTour?.tourId));
  }
}

/**
 * Root saga for audio module
 */
export default function* audioSaga() {
  yield takeLatest(constants.PLAY_TRACK_REQUEST, playTrackSaga);
  yield takeLatest(constants.SEND_PLAYBACK_HEARTBEAT_REQUEST, sendHeartbeatSaga);
  yield takeLatest(constants.PLAY_NEXT_TRACK, playNextTrackSaga);
  yield takeLatest(constants.PLAY_PREVIOUS_TRACK, playPreviousTrackSaga);
}
